import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Menu, X, LogOut } from 'lucide-react';
import { USER_NAV_ITEMS } from './navItems';
import FoundingMemberBanner from '../FoundingMemberBanner';
import Logo from '../Logo';

// Desktop user dropdown menu
const UserDropdown = ({ user, isOpen, onToggle, onClose, onLogout }) => (
  <div className="relative">
    <button
      onClick={onToggle}
      className="flex items-center space-x-2 text-[#4b5563] hover:text-[#002fa7] font-medium transition-colors"
      data-testid="user-menu-button"
    >
      {user?.picture ? (
        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
      ) : (
        <div className="w-8 h-8 bg-[#002fa7] text-white flex items-center justify-center font-semibold">
          {user?.name?.charAt(0).toUpperCase()}
        </div>
      )}
      <span>{user?.name}</span>
    </button>
    {isOpen && (
      <div className="absolute right-0 mt-2 w-52 bg-white border border-[#e5e7eb] shadow-lg py-2" data-testid="user-dropdown">
        {USER_NAV_ITEMS.map(({ to, icon: Icon, label }) => (
          <Link
            key={to}
            to={to}
            className="flex items-center space-x-2 px-4 py-2 text-sm text-[#4b5563] hover:bg-[#f4f5f7] hover:text-[#0f1115] transition-colors"
            onClick={onClose}
            data-testid={`dropdown-${label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Icon size={16} /><span>{label}</span>
          </Link>
        ))}
        <div className="my-1 border-t border-[#e5e7eb]" />
        <button
          onClick={onLogout}
          className="flex items-center space-x-2 px-4 py-2 w-full text-left text-sm text-[#4b5563] hover:bg-[#fef2f2] hover:text-[#e11d48] transition-colors"
          data-testid="dropdown-logout"
        >
          <LogOut size={16} /><span>Logout</span>
        </button>
      </div>
    )}
  </div>
);

// Mobile navigation menu
const MobileMenu = ({ isAuthenticated, onClose, onLogout }) => (
  <div className="md:hidden py-4 border-t border-[#e5e7eb]" data-testid="mobile-menu">
    <nav className="flex flex-col space-y-4">
      <Link to="/features" className="text-[#4b5563] hover:text-[#002fa7] font-medium" onClick={onClose}>Features</Link>
      <Link to="/coach" className="text-[#4b5563] hover:text-[#002fa7] font-medium" onClick={onClose}>Sage</Link>
      <Link to="/pricing" className="text-[#4b5563] hover:text-[#002fa7] font-medium" onClick={onClose}>Pricing</Link>
      <Link to="/blog" className="text-[#4b5563] hover:text-[#002fa7] font-medium" onClick={onClose}>Blog</Link>
      {isAuthenticated ? (
        <>
          <div className="border-t border-[#e5e7eb] pt-4 flex flex-col space-y-4">
            {USER_NAV_ITEMS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className="text-[#4b5563] hover:text-[#002fa7] font-medium"
                onClick={onClose}
              >
                {label}
              </Link>
            ))}
            <button onClick={onLogout} className="flex items-center gap-2 text-left text-[#e11d48] font-medium">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </>
      ) : (
        <>
          <Link to="/login" className="text-[#4b5563] hover:text-[#002fa7] font-medium" onClick={onClose}>Login</Link>
          <Link to="/free-ats-check" className="btn-primary text-center" onClick={onClose}>Free ATS Check</Link>
        </>
      )}
    </nav>
  </div>
);

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const headerRef = useRef(null);

  // Publish the actual measured header height (including the optional
  // FoundingMemberBanner, mobile menu expansion, etc.) as a CSS variable
  // on <html>. The Sidebar and MainContent read this via var(--header-h)
  // so they stay correctly offset even when the banner shows/hides.
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const publish = () => {
      document.documentElement.style.setProperty('--header-h', `${el.offsetHeight}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener('resize', publish);
    return () => { ro.disconnect(); window.removeEventListener('resize', publish); };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header ref={headerRef} className="header-glass fixed top-0 left-0 right-0 z-50" data-testid="header">
      <FoundingMemberBanner />
      {/* Full-width container so the logo anchors to the actual left edge of
          the viewport (with sensible padding). When the sidebar is open, this
          keeps the logo aligned directly above the sidebar's left edge instead
          of floating in dead space inside a max-w-7xl centered band. */}
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link" aria-label="Ascend — Home">
            {/* Brand logo. Since the badge already contains the "ASCEND"
                wordmark, we render the image alone at a size that gives the
                header proper visual weight. The slight negative margin trims
                the logo's internal whitespace so the header doesn't feel
                top-heavy. */}
            <Logo size={56} priority className="-my-1" />
            {/* SR-only fallback for assistive tech and any browser blocking the image */}
            <span className="sr-only">Ascend</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/features" className="text-[#4b5563] hover:text-[#002fa7] font-medium transition-colors" data-testid="nav-features">Features</Link>
            <Link to="/coach" className="text-[#4b5563] hover:text-[#002fa7] font-medium transition-colors" data-testid="nav-sage">Sage</Link>
            <Link to="/pricing" className="text-[#4b5563] hover:text-[#002fa7] font-medium transition-colors" data-testid="nav-pricing">Pricing</Link>
            <Link to="/blog" className="text-[#4b5563] hover:text-[#002fa7] font-medium transition-colors" data-testid="nav-blog">Blog</Link>
            {isAuthenticated ? (
              <UserDropdown
                user={user}
                isOpen={userMenuOpen}
                onToggle={() => setUserMenuOpen(!userMenuOpen)}
                onClose={() => setUserMenuOpen(false)}
                onLogout={handleLogout}
              />
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login" className="text-[#4b5563] hover:text-[#002fa7] font-medium transition-colors" data-testid="nav-login">Login</Link>
                <Link to="/free-ats-check" className="btn-primary" data-testid="nav-free-ats-check">Free ATS Check</Link>
              </div>
            )}
          </nav>

          <button className="md:hidden text-[#0f1115]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-button">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <MobileMenu
            isAuthenticated={isAuthenticated}
            onClose={() => setMobileMenuOpen(false)}
            onLogout={handleLogout}
          />
        )}
      </div>
    </header>
  );
};

export default Header;
